import { Component, effect, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    ActivatedRoute,
    NavigationExtras,
    Router,
    UrlSerializer,
    RouterLink
} from '@angular/router';
import { DropdownDirective } from '@app/shared/directives/dropdown.directive';
import { TranslateHelper } from '@app/_helpers';
import {
    AuthenticationService,
    CartService,
    QuotationCartService,
    UserService
} from '@app/_services';
import { DirectionService } from '@app/_services/direction.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CityDialogComponent } from '../header/components/city-dialog/city-dialog.component';
import {
    DropcartType,
    DropcartComponent
} from './components/dropcart/dropcart.component';
import { OffcanvasCartService } from '@app/_services/offcanvas-cart.service';
import { SiteService } from '@app/_services/site.service';
import { SharedService } from '@app/_services/shared.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { AccountMenuComponent } from './components/account-menu/account-menu.component';
import { IndicatorComponent } from './components/indicator/indicator.component';
import { SearchComponent } from '../../shared/components/search/search.component';
import { SectorsMenu } from '../../_components/sectors-menu/sectors-menu.component';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { DropdownDirective as DropdownDirective_1 } from '../../shared/directives/dropdown.directive';
import { QButtonComponent } from '../quotation-requests/components/button/button.component';
import { MatIconModule } from '@angular/material/icon';
import { CountrySelectComponent } from '@app/_components/country-select/country-select.component';

@Component({
    selector: 'app-header-new',
    templateUrl: './header-new.component.html',
    styleUrls: ['./header-new.component.scss'],
    providers: [TranslateHelper],
    standalone: true,
    imports: [
        RouterLink,
        DropdownDirective_1,
        IconComponent,
        SectorsMenu,
        SearchComponent,
        IndicatorComponent,
        AccountMenuComponent,
        NgIf,
        DropcartComponent,
        MatProgressSpinnerModule,
        NgFor,
        AsyncPipe,
        TranslateModule,
        QButtonComponent,
        MatIconModule,
        CountrySelectComponent
    ]
})
export class HeaderNewComponent implements OnInit {
    currentUser;
    currentCountry;
    currentFlag;
    currentPage;
    selectedZone;
    countries = [];
    dropcartType: DropcartType;
    destroy$: Subject<void> = new Subject<void>();

    background = '#ffffff';
    foreground = '#000000';
    notifications = this.authService.notifications;
    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private direction: DirectionService,
        private authService: AuthenticationService,
        private userService: UserService,
        private offcanvasCart: OffcanvasCartService,
        private dialog: MatDialog,
        private urlSerializer: UrlSerializer,
        public cart: CartService,
        public quotationCart: QuotationCartService,
        public translateHelper: TranslateHelper,
        public translateService: TranslateService,
        public siteService: SiteService,
        private sharedService: SharedService
    ) {
        this.location = this.sharedService.location;
    }
    location;
    // Dropdown refs to ensure only one is open at a time
    @ViewChild('sectorsDropdown', { read: DropdownDirective })
    sectorsDropdownRef: DropdownDirective;
    @ViewChild('rfqDropdown', { read: DropdownDirective })
    rfqDropdownRef: DropdownDirective;

    // Open/close helpers to keep dropdowns mutually exclusive
    toggleSectors() {
        const sectors = this.sectorsDropdownRef;
        const rfq = this.rfqDropdownRef;
        if (sectors?.isOpen) {
            sectors.close();
        } else {
            rfq?.close();
            sectors?.open();
        }
    }

    openRfq() {
        // Always close sectors when opening RFQ
        this.sectorsDropdownRef?.close();
        this.rfqDropdownRef?.open();
    }
    ngOnInit(): void {
        this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
            this.dropcartType = data.dropcartType || 'dropdown';
        });

        this.authService.currentUser.pipe().subscribe((user: any): any => {
            this.currentUser = user;
        });

        this.authService.currentZone.subscribe(zone => {
            if (!zone) return;
            if (!this.countries || this.countries.length == 0)
                this.authService.getCountries().subscribe(response => {
                    if (response?.code == 200) {
                        this.countries = response.data;
                        this.getUserCountry(zone);
                    }
                });
            else {
                this.getUserCountry(zone);
            }
        });

        this.sharedService.page$.subscribe(page => {
            this.currentPage = page;
        });

        this.background = this.siteService.siteSetting?.header?.backgroundColor;
        this.foreground = this.siteService.siteSetting?.header?.foregroundColor;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    getUserCountry(zone) {
        this.selectedZone = zone;
        let countryId = zone?.country?._id || this.countries[0]?._id;
        let userCountry = this.countries.find(
            country => country._id == countryId
        );
        this.currentCountry = userCountry;
        this.currentFlag =
            'assets/images/flags/' +
            (this.currentCountry?.code ?? 'earth') +
            '.png';
    }

    changeCountry(country, languageDropdown: DropdownDirective) {
        languageDropdown?.toggle(false);
        this.citiesModal(country);
    }

    loadCountries(languageDropdown: DropdownDirective) {
        if (!this.countries || this.countries.length == 0) {
            this.authService.getCountries().subscribe(response => {
                if (response?.code == 200) {
                    this.countries = response.data;
                }
            });
        }
        languageDropdown.toggle();
    }

    citiesModal(country): void {
        this.dialog
            .open(CityDialogComponent, {
                data: { country: country }
            })
            .afterClosed()
            .subscribe(
                response => {
                    if (response) {
                        this.authService.setUserZone(response);
                        this.selectedZone = response;
                    }
                },
                error => {}
            );
    }

    changeLangage(lang: string) {
        this.authService.currentUser.subscribe((user: any): any => {
            if (user?.preferances?.language != lang) {
                this.userService.setLanguage({ lang: lang }).subscribe(
                    res => {
                        if (res.code == 200) {
                            this.translateService.use(lang);

                            let navigationExtras: NavigationExtras;

                            const tree = this.router.parseUrl(this.router.url);

                            let url: any = this.urlSerializer.parse(
                                this.router.url
                            );

                            let stateUrl = this.router.url.split('/');
                            let commands = [];

                            stateUrl.forEach(segment => {
                                if (
                                    segment == '' ||
                                    segment == 'en' ||
                                    segment == 'ar'
                                )
                                    return;

                                if (segment.includes('?')) {
                                    commands.push(segment.split('?')[0]);
                                    if (
                                        !_.isEmpty(url.queryParams) ||
                                        !_.isEmpty(tree.queryParams)
                                    ) {
                                        //commands.push({queryParams: url.queryParams??tree.queryParams});
                                        navigationExtras = {
                                            queryParams:
                                                url.queryParams ??
                                                tree.queryParams
                                        };
                                    }
                                } else {
                                    commands.push(segment);
                                }
                            });

                            commands.unshift('/'.concat(lang));

                            this.router.navigate(commands, navigationExtras);

                            lang == 'ar'
                                ? (this.direction.value = 'rtl')
                                : 'ltr';
                        } else {
                            //console.log("Error setting user prefered language");
                        }
                    },
                    error => {
                        //console.log("Error setting user prefered language");
                    }
                );
            }
        });
    }

    onCartClick(): void {
        if (this.dropcartType === 'offcanvas') {
            this.offcanvasCart.open();
        }
    }
}
